import { useEffect, useState } from "react";
import TEXT from "../../../utils/globalText";
import "../../../index.css";

/**
 * @typedef {Object} PaymentMethod
 * @property {string} key - Unique identifier for the method (e.g., 'credit_card').
 * @property {string} label - The main display name (e.g., 'Credit Card').
 * @property {string} [subText] - Secondary text displayed below the label.
 * @property {boolean} active - If true, this method is currently selected.
 * @property {string[]} [notes] - Bullet points shown in the content area.
 * @property {Function} [onClick] - Function to call when this method is selected.
 */

/**
 * A tab-style selector used to switch between payment methods.
 * Supports animations, preview mode, and method notes.
 *
 * @param {Object} props
 * @param {string} props.title - Section title shown above options
 * @param {Array<{
 *    key: string,
 *    label: string,
 *    subText?: string,
 *    active: boolean,
 *    notes?: string[],
 *    onClick?: Function
 * }>} props.methods - Payment method options
 * @param {boolean} [props.slideAnimation=true] - Enables animation on change
 * @param {boolean} [props.previewMode=false] - If true, shows read-only summary
 * @param {string|null} [props.selectedMethod=null] - Selected method for preview
 */

const PaymentMethodSelector = ({
  title,
  methods = [],
  slideAnimation = true,
  previewMode = false,
  selectedMethod = null,
}) => {
  const methodCount = methods.length;
  const activeIndexRaw = methods.findIndex((m) => m.active);
  const activeIndex = activeIndexRaw < 0 ? 0 : activeIndexRaw;
  const activeMethod = methods[activeIndex];

  const [prevIndex, setPrevIndex] = useState(activeIndex);
  const [direction, setDirection] = useState("none");

  useEffect(() => {
    if (prevIndex !== activeIndex) {
      setDirection(activeIndex > prevIndex ? "right" : "left");
      setPrevIndex(activeIndex);
    }
  }, [activeIndex, prevIndex]);

  const tabWidth = `${100 / methodCount}%`;

  const highlightStyle = {
    width: tabWidth,
    left: `${activeIndex * (100 / methodCount)}%`,
  };

  if (previewMode && selectedMethod) {
    return (
      <div className="global-card-create">
        <p className="text-text-primary font-semibold text-font-md mb-margin-md !capitalize">
          {TEXT.PAYMENT_DETAILS}
        </p>

        <div className="flex flex-col justify-between gap-y-gap-xs">
          <p className="font-normal text-font-md text-text-primary">
            {selectedMethod.toUpperCase()}
          </p>
          <p className="font-normal text-font-xs text-text-secondary">
            {TEXT.PAYMENT_METHOD}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-primary-card rounded-border-radius-md"
      style={{ overflow: "hidden" }}
    >
      <p className="text-text-primary p-padding-md text-font-md font-semibold">
        {title}
      </p>

      <div className="w-full flex relative bg-primary-nav text-center rounded-border-radius-md overflow-hidden">
        <div
          className={`absolute top-top-base h-full bg-positive-act transition-highlight 
            ${direction === "right" ? "highlight-slide-right" : ""} 
            ${direction === "left" ? "highlight-slide-left" : ""}`}
          style={highlightStyle}
        />

        {methods.map(({ key, label, subText, active, onClick }) => (
          <div
            key={key}
            onClick={!active ? onClick : undefined}
            style={{
              width: tabWidth,
            }}
            className={`flex flex-col items-center justify-center py-padding-sm px-padding-md relative z-z-index-base text-text-primary ${
              active
                ? "cursor-default"
                : "cursor-not-allowed opacity-light-opacity"
            }`}
          >
            <h6 className="text-font-md font-semibold text-text-primary">
              {label}
            </h6>
            <div className="w-stepper-btn-width">
              {subText && <p className="text-font-xs">{subText}</p>}
            </div>
          </div>
        ))}
      </div>
      <div
        key={activeIndex}
        className={`p-padding-md ${
          slideAnimation && direction === "right" ? "animate-enter-right" : ""
        } ${
          slideAnimation && direction === "left" ? "animate-enter-left" : ""
        }`}
      >
        <h6 className="text-font-md font-semibold mb-margin-sm text-text-primary">
          {activeMethod?.label}
        </h6>

        {activeMethod?.notes?.map((note, i) => (
          <p
            key={note.key}
            className="text-font-xs text-text-secondary mb-margin-sm last:mb-margin-base"
          >
            • {note}
          </p>
        ))}
      </div>
    </div>
  );
};

export default PaymentMethodSelector;
